import React, { ReactNode } from 'react';

import { Box } from '@mui/material';
import { Panel, PanelContent, PanelTitle } from '@apigrowfinance/pbolt-panel';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { PieChart, Pie, Cell } from 'recharts';

import CustomLegend from '@components/overviewPanels/limitPiePanel/Legend';
import { useAccount } from '@contexts/AccountProvider';

export interface Limit {
    name: ReactNode;
    value: number;
    id: string;
}

interface LimitPiePanelProps {
    title: ReactNode;
    isLoadingLimits: boolean;
    limitsToPlot: Limit[];
    legendValuesNotPlotted?: Limit[];
}

const LimitPiePanel = ({
    title,
    limitsToPlot,
    legendValuesNotPlotted,
    isLoadingLimits,
}: LimitPiePanelProps) => {
    const { theme } = useTheme();
    const { program, isLoading: isLoadingAccount } = useAccount();
    const COLORS = [
        theme.palette.info.main,
        theme.palette.primary.main,
        theme.palette.text.primary,
    ];
    const isLoading = isLoadingLimits || isLoadingAccount;

    // Clone array of objects which contain react nodes, so we can add a loading entry if needed
    const data = limitsToPlot.map((a) => {
        return { ...a };
    });
    let somethingToPlot = true;
    if (isLoading) {
        data.push({ value: 100, name: 'loader', id: 'loader' });
    } else {
        somethingToPlot = false;
        data.forEach((anItem) => {
            if (anItem.value > 0) {
                somethingToPlot = true;
            }
        });
    }

    return (
        <Panel>
            <PanelTitle loading={isLoading} hideArrows>
                {title}
            </PanelTitle>
            <PanelContent>
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        minWidth: theme.spacing(35),
                    }}>
                    <Box display="flex" flexDirection="row">
                        <Box
                            minHeight={theme.spacing(15)}
                            display="flex"
                            mt={1.75}>
                            <PieChart
                                height={120}
                                width={120}
                                data-testid="chart"
                                margin={{
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0,
                                }}>
                                <Pie
                                    isAnimationActive={!isLoading}
                                    stroke=""
                                    data={data}
                                    dataKey="value"
                                    nameKey="name"
                                    outerRadius={60}
                                    innerRadius={54}
                                    fill="#f00">
                                    {data.map((entry, index) => (
                                        <Cell
                                            key={`cell-${entry?.id.toLowerCase()}`}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    ))}
                                </Pie>
                                {!somethingToPlot && (
                                    <Pie
                                        dataKey="value"
                                        outerRadius={60}
                                        innerRadius={54}
                                        stroke=""
                                        data={[
                                            {
                                                name: 'No Data',
                                                value: 1,
                                                id: 'no-data',
                                            },
                                        ]}
                                        fill={COLORS[0]}
                                    />
                                )}
                            </PieChart>
                        </Box>
                        <CustomLegend
                            isLoading={isLoading}
                            limits={data}
                            legendValuesNotPlotted={legendValuesNotPlotted}
                            colors={COLORS}
                            currencyCode={program?.currency_numeric_code}
                        />
                    </Box>
                </Box>
            </PanelContent>
        </Panel>
    );
};
export default LimitPiePanel;
